import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { AoParams, Bootstrap, BuildingConsumption, PlanGeo } from './types';

async function fetchBootstrap(): Promise<Bootstrap> {
  const res = await fetch('/api/plan/creation/bootstrap');
  if (!res.ok) throw new Error('bootstrap');
  return res.json();
}

export function useBootstrap() {
  return useQuery({ queryKey: ['plan-creation-bootstrap'], queryFn: fetchBootstrap, staleTime: Infinity });
}

/** Degree-day correction of the reference weather station (simplified demo model). */
const STATION_FACTOR: Record<string, number> = {
  'Paris-Montsouris': 1,
  Orly: 1.03,
  'Roissy-Charles-de-Gaulle': 1.06,
  'Melun-Villaroche': 1.05,
};

const YEAR_FACTOR: Record<string, number> = {
  'Année de référence 1991-2020': 1,
  '2023 (année réelle)': 0.96,
  '2022 (année réelle)': 0.93,
  '2021 (année réelle)': 1.02,
};

const RESIDENTIAL = ['Habitat collectif', 'Habitat individuel'];

/** Reference intensity used by UrbanMap for buildings without a DPE (kWh/m²/an). */
const REFERENCE_INTENSITY = 200;

export function weatherFactor(p: AoParams): number {
  return (STATION_FACTOR[p.weatherStation] ?? 1) * (YEAR_FACTOR[p.weatherYear] ?? 1);
}

/**
 * Recalculates the consumption of every building of the perimeter with the AO hypotheses.
 * The result legitimately differs from UrbanMap (PRD US-002).
 */
export function recalculate(geo: PlanGeo, params: AoParams): BuildingConsumption[] {
  const weather = weatherFactor(params);
  return geo.buildings.features.map((f) => {
    const p = f.properties ?? {};
    const umHeat = Number(p.heat) || 0;
    const measured = p.measured === 1;
    const usage = String(p.usage ?? '');
    const ratio = (RESIDENTIAL.includes(usage) ? params.ratioResidential : params.ratioTertiary) / 100;
    // A measured SNCU consumption is not re-estimated: only the weather correction applies.
    const intensity = measured ? 1 : params.intensityNoDpe / REFERENCE_INTENSITY;
    return {
      functionalId: String(p.functionalId ?? ''),
      address: String(p.address ?? 'Adresse inconnue'),
      usageLabel: String(p.usageLabel ?? usage ?? 'Usage inconnu'),
      totalArea: p.totalArea === undefined ? null : Number(p.totalArea),
      umHeat,
      planHeat: Math.round(umHeat * weather * ratio * intensity),
      measured,
    };
  });
}

export interface ConsumptionImpact {
  umTotal: number;
  planTotal: number;
  deltaPct: number;
  buildings: number;
}

export function useImpact(geo: PlanGeo | undefined, params: AoParams): ConsumptionImpact | null {
  return useMemo(() => {
    if (!geo) return null;
    const rows = recalculate(geo, params);
    const umTotal = rows.reduce((s, r) => s + r.umHeat, 0);
    const planTotal = rows.reduce((s, r) => s + r.planHeat, 0);
    return {
      umTotal: Math.round(umTotal),
      planTotal: Math.round(planTotal),
      deltaPct: umTotal > 0 ? Math.round(((planTotal - umTotal) / umTotal) * 1000) / 10 : 0,
      buildings: rows.length,
    };
  }, [geo, params]);
}

/** True when the AO hypotheses are complete enough to create the project (PRD US-001). */
export function paramsComplete(p: AoParams): boolean {
  return Boolean(p.weatherStation) && Boolean(p.weatherYear)
    && p.ratioResidential > 0 && p.ratioTertiary > 0 && p.intensityNoDpe > 0;
}
